import { useQuery } from '@tanstack/react-query';
import { api, clinicApi } from '@/lib/api';
import { useAuth } from './useAuth';
import { useCan } from './usePermissions';
import type { DashboardActivity, DashboardSummary } from '@/types/api';

export const DASHBOARD_KEY = ['dashboard', 'summary'] as const;
export const DASHBOARD_ACTIVITY_KEY = ['dashboard', 'activity'] as const;

/**
 * Home-page totals. Keyed by active clinic so switching clinics — once the
 * product supports that — won't show stale numbers.
 */
export function useDashboardSummary(opts: { enabled?: boolean } = {}) {
  const { activeClinic, isAuthenticated } = useAuth();
  const can = useCan();
  // Gated the same way as the activity feed below. Without this a member whose
  // role omits `dashboard.read` lands on the home page — the redirect target
  // for every permission denial — and is met with an error card reporting a
  // 403 they can do nothing about.
  const enabled =
    (opts.enabled ?? true) && isAuthenticated && !!activeClinic && can('dashboard.read');
  return useQuery({
    queryKey: [...DASHBOARD_KEY, activeClinic?.id ?? null],
    queryFn: () => clinicApi.get<DashboardSummary>('dashboard/summary'),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * The recent-activity strip.
 *
 * A second request rather than a field on the summary, because it answers to
 * its own permission (`dashboard.activity.read`) — the feed names who dispensed
 * what, which is not the same disclosure as a stock count.
 *
 * Gated by `enabled` so a member without the key never fires a request that
 * would only 403. The server enforces it regardless; this just avoids the noise.
 */
export function useDashboardActivity(opts: { enabled?: boolean } = {}) {
  const { activeClinic, isAuthenticated } = useAuth();
  const can = useCan();
  const enabled =
    (opts.enabled ?? true) &&
    isAuthenticated &&
    !!activeClinic &&
    can('dashboard.activity.read');
  return useQuery({
    queryKey: [...DASHBOARD_ACTIVITY_KEY, activeClinic?.id ?? null],
    queryFn: () => clinicApi.get<DashboardActivity[]>('dashboard/activity'),
    enabled,
    staleTime: 30_000,
  });
}
